import React from "react"
import { Button, Checkbox, Switch, Tooltip } from "antd"
import SignificanceControl from "shared/ui/Significance/SignificanceControl.component"
import { LOCKED_STAT_REASON } from "shared/ui/Builder"
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { SignificanceSettings } from "types/significance.types"
import { MEAN_STAT_NAMES, MEAN_SUMMARY_STATS, meanSummaryStats } from "../../compiler/table"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import {
  DEFAULT_TAB_SPEC_SIGNIFICANCE,
  MEAN_EXCLUDING_ZERO,
  MEAN_STAT_TYPES,
  MeanStat,
} from "../../types/tabSpecDocument"
import Field from "./Field.component"
import { useInspectedTable } from "./useInspectedTable"

/** A statistic, or the mean excluding 0 that a number table lists among them. */
type StatValue = STATISTIC_TYPE | typeof MEAN_EXCLUDING_ZERO
type StatOption = { value: StatValue; label: string }

const CHOICE_STATS: StatOption[] = [
  { value: STATISTIC_TYPE.COL_PERCENT, label: "Column %" },
  { value: STATISTIC_TYPE.N, label: "Count" },
  { value: STATISTIC_TYPE.ROW_PERCENT, label: "Row %" },
  { value: STATISTIC_TYPE.INDEX, label: "Index" },
]
const MEAN_STAT_OPTIONS = MEAN_STAT_TYPES.map((value) => ({ value, label: MEAN_STAT_NAMES[value] }))
/** A number table's list: what is shown under a mean comes right after the mean. */
const NUMBER_STATS: StatOption[] = [
  { value: STATISTIC_TYPE.AVG, label: "Mean" },
  ...MEAN_STAT_OPTIONS,
  { value: STATISTIC_TYPE.MIN, label: "Minimum" },
  { value: STATISTIC_TYPE.MAX, label: "Maximum" },
  { value: STATISTIC_TYPE.BASE_N, label: "Base" },
]
/** What each grid row of a summary of means can show. */
const MEAN_SUMMARY_OPTIONS: StatOption[] = MEAN_SUMMARY_STATS.map((s) => ({
  value: s.stat,
  label: s.display,
}))
/** A grid summary shows each row's own base above its value when this is ticked. */
const ROW_BASE: StatOption = { value: STATISTIC_TYPE.BASE_N, label: "Base of each row" }
const MEAN_SUMMARY_HINT =
  "Each grid row shows the statistics ticked here, and the people it counts when Base of each row is ticked."
const SUMMARY_HINT =
  "Each grid row shows what the Rows tab chooses, and the people it counts when Base of each row is ticked."

/** The chosen statistics in the order of the options, so the table lists them the same way. */
const inOrder = (options: StatOption[], chosen: StatValue[]): StatValue[] =>
  options.map((o) => o.value).filter((v) => chosen.includes(v))
const isStatistic = (v: StatValue): v is STATISTIC_TYPE => v !== MEAN_EXCLUDING_ZERO

/**
 * The statistics of the table and its significance testing. Both follow the
 * spec's defaults until set here. Significance needs the column percentage
 * of a choice table, so that one cannot be unticked while testing is on. A
 * grid summary of means picks what each grid row shows (at least one), a
 * table with a mean row what is shown under the mean, and a number table
 * lists it among its statistics. Every grid summary can show each row's own
 * base ("base n" in its list).
 */
const StatisticsTab: React.FC<{ tableId: string }> = ({ tableId }) => {
  const inspected = useInspectedTable(tableId)
  const inherited = useTabSpecStore((s) => s.document.defaults.significance)
  const updateTable = useTabSpecStore((s) => s.updateTable)
  const clearOverrides = useTabSpecStore((s) => s.clearOverrides)
  if (!inspected) return null
  const { table, resolved } = inspected

  const numeric = table.kind === "numeric"
  const gridSummary = table.kind === "grid-summary"
  const meanSummary = resolved.summary?.kind === "mean"
  const options = numeric
    ? NUMBER_STATS
    : meanSummary
      ? [...MEAN_SUMMARY_OPTIONS, ROW_BASE]
      : gridSummary
        ? [ROW_BASE]
        : CHOICE_STATS
  const locked = !numeric && !gridSummary && resolved.significance !== undefined
  const excl0 = numeric && resolved.meanStats.includes(MEAN_EXCLUDING_ZERO)
  const rowBase = gridSummary && resolved.statistics.includes(STATISTIC_TYPE.BASE_N)
  // A summary of means shows what the compiler shows: the mean when its list names nothing else.
  const meanShown = meanSummary ? meanSummaryStats(resolved.statistics).map((s) => s.stat) : []
  const chosen: StatValue[] = gridSummary
    ? [...meanShown, ...(rowBase ? [STATISTIC_TYPE.BASE_N] : [])]
    : locked && !resolved.statistics.includes(STATISTIC_TYPE.COL_PERCENT)
      ? [STATISTIC_TYPE.COL_PERCENT, ...resolved.statistics]
      : excl0
        ? [...resolved.statistics, MEAN_EXCLUDING_ZERO]
        : resolved.statistics
  /**
   * Writes the ticked statistics. A number table keeps the mean excluding 0
   * in its `meanStats` (with a list of its own it shows it by that alone):
   * written when it is ticked, taken away when it was set and is not, so a
   * table that never had it saves as before.
   */
  const pick = (next: StatValue[]): void => {
    const picked = inOrder(options, next)
    const statistics = picked.filter(isStatistic)
    const meanStats: MeanStat[] | undefined = picked.includes(MEAN_EXCLUDING_ZERO)
      ? [MEAN_EXCLUDING_ZERO]
      : undefined
    const withMeanStats = numeric && (meanStats !== undefined || table.meanStats !== undefined)
    updateTable(tableId, { statistics, ...(withMeanStats ? { meanStats } : {}) })
  }
  const shown = options.map((o) =>
    locked && o.value === STATISTIC_TYPE.COL_PERCENT
      ? {
          value: o.value,
          disabled: true,
          label: (
            <Tooltip title={LOCKED_STAT_REASON}>
              <span>{o.label}</span>
            </Tooltip>
          ),
        }
      : // Each grid row of a summary of means shows at least one statistic.
        meanSummary && meanShown.length === 1 && meanShown[0] === o.value
        ? { ...o, disabled: true }
        : o
  )
  // Choice tables and scales show the statistics under the mean only with a mean row.
  const hasMeanRow =
    !numeric && table.kind !== "grid-summary" && resolved.rows.some((r) => r.kind === "mean")

  const own = table.significance
  const off = own === "off" || (own !== undefined && !own.enabled)
  // The control shows a table switched off as its own "Off".
  const controlValue: SignificanceSettings | undefined =
    own === "off"
      ? { ...(inherited ?? DEFAULT_TAB_SPEC_SIGNIFICANCE), enabled: false }
      : own

  return (
    <div className="ts-tab">
      <Field
        label="Statistics"
        extra={
          <Button
            type="link"
            size="small"
            className="ts-link"
            disabled={table.statistics === undefined && (!numeric || table.meanStats === undefined)}
            onClick={() =>
              clearOverrides(tableId, numeric ? ["statistics", "meanStats"] : ["statistics"])
            }
          >
            Use defaults
          </Button>
        }
      >
        {gridSummary && <p className="ts-hint">{meanSummary ? MEAN_SUMMARY_HINT : SUMMARY_HINT}</p>}
        <Checkbox.Group
          options={shown}
          value={chosen}
          onChange={(next) => pick(next as StatValue[])}
        />
      </Field>
      {hasMeanRow && (
        <Field
          label="Under the mean, also show"
          extra={
            <Button
              type="link"
              size="small"
              className="ts-link"
              disabled={table.meanStats === undefined}
              onClick={() => clearOverrides(tableId, ["meanStats"])}
            >
              Follow the defaults
            </Button>
          }
        >
          <Checkbox.Group
            aria-label="Under the mean, also show"
            options={MEAN_STAT_OPTIONS}
            value={resolved.meanStats}
            onChange={(next) =>
              // An empty list is kept: none, whatever the defaults say.
              updateTable(tableId, {
                meanStats: MEAN_STAT_TYPES.filter((s) => (next as MeanStat[]).includes(s)),
              })
            }
          />
        </Field>
      )}
      <Field label="Significance">
        <SignificanceControl
          value={controlValue}
          allowInherit
          inherited={inherited}
          tableType={numeric || gridSummary ? "DTAB" : "XTAB"}
          helperPlacement="below"
          onChange={(next) => {
            if (next === undefined) clearOverrides(tableId, ["significance"])
            else if (!next.enabled) updateTable(tableId, { significance: "off" })
            else updateTable(tableId, { significance: next })
          }}
        />
        <div className="ts-field__row">
          <span>Off for this table</span>
          <Switch
            size="small"
            aria-label="Off for this table"
            checked={off}
            onChange={(checked) =>
              checked
                ? updateTable(tableId, { significance: "off" })
                : clearOverrides(tableId, ["significance"])
            }
          />
        </div>
      </Field>
    </div>
  )
}

export default StatisticsTab
